/*
 * Developed for G.J. Gardner Homes by Softeq Development Corporation
 * http://www.softeq.com
 */

import { NgModule } from '@angular/core';
import { RouterModule } from '@angular/router';
import { APPLICATION_VIEW_OFFICE } from '@gh/builder-shared-data';
import { createRoute } from '@gh/builder-shared';
import { CoreLazyModule, LAZY_SYMBOLS, lazyActivatorRoute } from '@gh/core-lazy';
import { BulkDeleteHpBusinessService, BulkDeleteHpComponent } from './bulk-delete-hp';
import { BulkUpdateBusinessService, BulkUpdateContainerComponent } from './bulk-update';

import {
  formulaPropagationDialogRoute,
  formulaPropagationHpListDialogRoute,
  HousePlanEstimateDetailsBusinessService,
  HousePlanEstimateDetailsComponent,
  HousePlanListBusinessService,
  HousePlanListComponent,
} from './house-plans';
import {
  PriceBookBulkUpdateICComponent,
  PriceBookDetailsBusinessService,
  PriceBookDetailsComponent,
  PriceBookLinkBusinessService,
  PriceBookLinkComponent,
  PriceBookListBusinessService,
  PriceBookListComponent,
  PriceBookBulkUpdateHPComponent,
} from './price-books';
import { PbLinkEntryPoint } from './price-books/resources/price-book-link.model';
import { JobEstimatesJobListComponent } from './job-estimates-job-list/job-estimates-job-list.component';
import { JobEstimatesJobListBusinessService } from './job-estimates-job-list/resources/job-estimates-job-list-business.service';

const routes = [
  lazyActivatorRoute,
  createRoute({
    path: 'house-plans',
    component: HousePlanListComponent,
    init: HousePlanListBusinessService,
    auth: {
      applicationView: APPLICATION_VIEW_OFFICE,
      permission: 'Estimating.HouseEstimate.Plans.LIST',
    },
  }),
  createRoute({
    path: 'job-estimates',
    component: JobEstimatesJobListComponent,
    init: JobEstimatesJobListBusinessService,
    auth: {
      applicationView: APPLICATION_VIEW_OFFICE,
      permission: 'Estimating.JobEstimate.LIST',
    },
  }),
  createRoute({
    path: 'price-books',
    component: PriceBookListComponent,
    init: PriceBookListBusinessService,
    auth: {
      applicationView: APPLICATION_VIEW_OFFICE,
      permission: 'Estimating.PriceBook.LIST',
    },
  }),
  createRoute({
    path: 'price-books/new',
    component: PriceBookDetailsComponent,
    init: PriceBookDetailsBusinessService,
    leave: true,
    auth: {
      applicationView: APPLICATION_VIEW_OFFICE,
      permission: 'Estimating.PriceBook.CREATE',
    },
  }),
  createRoute({
    path: 'price-books/:priceBookId',
    component: PriceBookDetailsComponent,
    init: PriceBookDetailsBusinessService,
    leave: true,
    auth: {
      applicationView: APPLICATION_VIEW_OFFICE,
      permission: ['Estimating.PriceBook.EDIT', 'Estimating.PriceBook.VIEW'],
    },
  }),
  createRoute({
    path: 'price-books/:priceBookId/links/jst',
    component: PriceBookLinkComponent,
    init: PriceBookLinkBusinessService,
    leave: true,
    auth: {
      applicationView: APPLICATION_VIEW_OFFICE,
      permission: ['Estimating.PriceBook.EDIT', 'Estimating.PriceBook.VIEW'],
    },
    static: {
      entryPoint: PbLinkEntryPoint.Jst,
    },
  }),
  createRoute({
    path: 'price-books/:priceBookId/links/csm',
    component: PriceBookLinkComponent,
    init: PriceBookLinkBusinessService,
    leave: true,
    auth: {
      applicationView: APPLICATION_VIEW_OFFICE,
      permission: ['Estimating.PriceBook.EDIT', 'Estimating.PriceBook.VIEW'],
    },
    static: {
      entryPoint: PbLinkEntryPoint.Csm,
    },
  }),
  createRoute({
                path: 'price-books/:priceBookId/bulk-update-hp-markups',
                component: PriceBookBulkUpdateHPComponent,
                leave: true,
                init: {
                  service: PriceBookDetailsBusinessService,
                  selector: 'initBulkUpdateHPMarkups',
                },
                auth: {
                  applicationView: APPLICATION_VIEW_OFFICE,
                  permission: ['Estimating.PriceBook.EDIT'],
                },
              }),
  createRoute({
                path: 'price-books/:priceBookId/bulk-update-ic-markups',
                component: PriceBookBulkUpdateICComponent,
                leave: true,
                init: {
                  service: PriceBookDetailsBusinessService,
                  selector: 'initBulkUpdateICPMarkups',
                },
                auth: {
                  applicationView: APPLICATION_VIEW_OFFICE,
                  permission: ['Estimating.PriceBook.EDIT'],
                },
              }),
  createRoute({
    path: 'bulk-update',
    component: BulkUpdateContainerComponent,
    init: BulkUpdateBusinessService,
    auth: {
      applicationView: APPLICATION_VIEW_OFFICE,
      permission: 'Estimating.BulkUpdate.MANAGE',
    },
    leave: true,
  }),
  createRoute({
    path: 'bulk-delete-hp',
    component: BulkDeleteHpComponent,
    init: BulkDeleteHpBusinessService,
    auth: {
      permission: 'Estimating.BulkDelete.HousePlans.MANAGE',
    },
  }),
  createRoute({
    path: 'house-plans/:housePlanId/estimate',
    component: HousePlanEstimateDetailsComponent,
    init: HousePlanEstimateDetailsBusinessService,
    leave: true,
    auth: {
      permission: [
        'Estimating.HouseEstimate.Estimate.CREATE_EDIT',
        'Estimating.HouseEstimate.Estimate.EDIT_QQ',
        'Estimating.HouseEstimate.Estimate.VIEW',
      ],
    },
  }),
  createRoute({
    path: 'house-plans/:housePlanId/estimate/new',
    component: HousePlanEstimateDetailsComponent,
    init: HousePlanEstimateDetailsBusinessService,
    leave: true,
    auth: {
      permission: ['Estimating.HouseEstimate.Estimate.CREATE_EDIT'],
    },
  }),
];

export function estimatingLazySymbolsFactory(): Hash<any> {
  return { formulaPropagationDialogRoute, formulaPropagationHpListDialogRoute };
}

@NgModule({
  imports: [
    RouterModule.forChild(routes),
    CoreLazyModule.forChild(),
  ],
  declarations: [],
  providers: [
    { provide: LAZY_SYMBOLS, useFactory: estimatingLazySymbolsFactory, deps: [] },
  ],
  exports: [RouterModule],
})
export class BuilderEstimatingRoutingModule {
}
